import type { MeshStyles, Style } from '@wix/thunderbolt-mesh'
import _ from 'lodash'
import { MESH_ID_SEPARATOR, skinPartId } from './meshConstants'
import { DEFAULT_DATA_MODE, getDefaultCompId, HOVER_DATA_MODE, HOVER_SUFFIX } from '@wix/thunderbolt-commons'
import { MeshStylesMapWithRepeater } from '../../types/css'
import { AutoHeightComponents, MeshSelectorsOptions } from '../../types/mesh'

const isContainerId = (meshId: string, containerIdRegex: RegExp) => containerIdRegex.test(meshId)
const isWedgeId = (meshId: string) => meshId.includes('wedge')
const isRotatedChildId = (rotatedComponents: Array<string>, meshId: string) => _.includes(rotatedComponents, meshId)
const isRotatedWrapperId = (meshId: string) => meshId.includes('rotated-wrapper')
const getRotatedWrappedCompId = (rotatedWrapperId: string) => rotatedWrapperId.replace('-rotated-wrapper', '')
const hasClass = (meshId: string) => meshId.includes(MESH_ID_SEPARATOR)

const idsToSelectors = (
	meshContainerId: string,
	originalStylesMap: MeshStyles,
	rotatedComponents: Array<string>,
	autoHeightComps: AutoHeightComponents,
	{ isChildOfRepeater = false, isHoverBox = false, isMeshCatharsisExperimentOpen = false }: MeshSelectorsOptions = {}
) => {
	const isHover = meshContainerId.endsWith(HOVER_SUFFIX)
	const hoverBoxPrefix = isHoverBox ? (isHover ? HOVER_DATA_MODE : DEFAULT_DATA_MODE) : ''
	const getMeshChildId = (meshId: string) => (autoHeightComps[meshId] ? getAutoHeightWrapperId(meshId) : meshId)

	meshContainerId = getDefaultCompId(meshContainerId)
	const containerIdRegex = new RegExp(`^(${meshContainerId})(inlineContent|inlineContent-gridContainer)$`)
	const containerRootRegex = new RegExp(`^(${meshContainerId})(-root)$`)

	const repeaterStylesMap = {} as _.Dictionary<Style>
	const stylesMap = _.mapKeys(originalStylesMap, (style: Style, meshId: string) => {
		const shouldAddClass = hasClass(meshId)
		const splitMeshId = _.split(meshId, MESH_ID_SEPARATOR)

		const originalMeshId = splitMeshId[0]
		meshId = getDefaultCompId(originalMeshId)

		const className = shouldAddClass ? `.${splitMeshId[1]}` : ''
		const meshContainerDisplayedOnlyIdPrefix = `${meshContainerId}__`
		const classSelector = shouldAddClass
			? `${hoverBoxPrefix}[id="${meshContainerId}"]${className} `
			: `${isHoverBox ? hoverBoxPrefix + ' ' : ''}`
		const repeaterClassSelector = shouldAddClass ? `[id^="${meshContainerDisplayedOnlyIdPrefix}"]${className} ` : ''

		if (containerRootRegex.test(meshId)) {
			return `#${meshContainerId}`
		}

		if (isContainerId(meshId, containerIdRegex)) {
			// meshId=container1inlineContent or container1inlineContent-gridContainer
			const suffix = _.replace(meshId, meshContainerId, '') // suffix=inlineContent or inlineContent-gridContainer or inlineContent-wedge-1

			const regularSelector = `${classSelector}[data-mesh-id=${meshId}]`
			const repeaterSelector = `${repeaterClassSelector}[data-mesh-id^="${meshContainerDisplayedOnlyIdPrefix}"][data-mesh-id$="${suffix}"]`

			if (isMeshCatharsisExperimentOpen) {
				repeaterStylesMap[repeaterSelector] = style
			}
			return isChildOfRepeater ? repeaterSelector : regularSelector
		}

		if (isWedgeId(meshId)) {
			// container1inlineContent-wedge-1
			const regularSelector = `${classSelector}[data-mesh-id=${meshId}]`
			const repeaterSelector = `${repeaterClassSelector}[data-mesh-id=${meshId}]`

			if (isMeshCatharsisExperimentOpen) {
				repeaterStylesMap[repeaterSelector] = style
			}
			return isChildOfRepeater ? repeaterSelector : regularSelector
		}

		if (isRotatedChildId(rotatedComponents, originalMeshId)) {
			// We need specific rule for rotated child because of repeater selector
			// meshId = child1
			const meshChildId = getMeshChildId(meshId)
			const regularSelector = `${classSelector}[data-mesh-id="${meshId}-rotated-wrapper"] > [id="${meshChildId}"]`
			const repeaterSelector = `${repeaterClassSelector}[data-mesh-id^="${meshId}"][data-mesh-id$="-rotated-wrapper"] > [id^="${meshChildId}__"]`

			if (isMeshCatharsisExperimentOpen) {
				repeaterStylesMap[repeaterSelector] = style
			}
			return isChildOfRepeater ? repeaterSelector : regularSelector
		}

		if (isRotatedWrapperId(meshId)) {
			// meshId = child1-rotated-wrapper
			const regularSelector = `${classSelector}[data-mesh-id=${meshId}]`
			const repeaterSelector = `${repeaterClassSelector}[data-mesh-id^="${getRotatedWrappedCompId(
				meshId
			)}"][data-mesh-id$="rotated-wrapper"]`

			if (isMeshCatharsisExperimentOpen) {
				repeaterStylesMap[repeaterSelector] = style
			}
			return isChildOfRepeater ? repeaterSelector : regularSelector
		}

		// meshId = child1
		// We need to add grid-container selector here because sometimes we calculate mesh but we don't render meshContainer
		const meshChildId = getMeshChildId(meshId)
		const regularSelector = `${classSelector}[data-mesh-id=${meshContainerId}${skinPartId}-gridContainer] > [id="${meshChildId}"]`
		const repeaterSelector = `${repeaterClassSelector}[data-mesh-id^=${meshContainerDisplayedOnlyIdPrefix}] > [id^="${meshChildId}__"]`

		if (isMeshCatharsisExperimentOpen) {
			repeaterStylesMap[repeaterSelector] = style
		}
		return isChildOfRepeater ? repeaterSelector : regularSelector
	})

	return isMeshCatharsisExperimentOpen ? { stylesMap, repeaterStylesMap } : stylesMap
}

const toCssKey = (key: string): string => {
	const isCssVar = key.startsWith('--')

	return isCssVar ? key : _.kebabCase(key)
}

export const getMeshStyles = (
	id: string,
	stylesMap: MeshStyles | MeshStylesMapWithRepeater,
	rotatedComponents: Array<string>,
	autoHeightComps: AutoHeightComponents,
	options?: MeshSelectorsOptions
): MeshStyles | MeshStylesMapWithRepeater => {
	// structure2mesh returns css properties in camelCase (for React). We have to transform them to kebab-case
	stylesMap = _.mapValues(stylesMap, (style) => _.mapKeys(style, (__, key) => toCssKey(key)))
	return idsToSelectors(id, stylesMap, rotatedComponents, autoHeightComps, options)
}

export const getAutoHeightWrapperId = (compId: string) => `wrapper-${compId}`
